// The numbers behind the Prop business page (BusinessPage.js): money spent (expenses.js on the
// server) against prop firm payouts (withdrawals from funded accounts), by firm and by month,
// and each prop account's life from start to pass, fail or today.
import { accountStatus } from './balance';
import { dayKey, netPnl } from './pnl';

export const CATEGORIES = ['evaluation', 'reset', 'activation', 'subscription', 'data', 'platform', 'education', 'other'];
export const CATEGORY_NAMES = {
  evaluation: 'Evaluation fee', reset: 'Reset', activation: 'Activation fee', subscription: 'Monthly account fee',
  data: 'Market data', platform: 'Platform or software', education: 'Courses, coaching and signals', other: 'Other',
};

// Prop firms recognized in account names (and TopstepX accounts are Topstep).
export const FIRMS = [
  ['Topstep', /top\s*step/i], ['Apex', /\bapex\b/i], ['MyFundedFutures', /my\s*funded\s*futures|\bmff(u)?\b/i], ['Lucid', /\blucid\b/i],
  ['Tradeify', /tradeify/i], ['TradeDay', /trade\s*day/i], ['Take Profit Trader', /take\s*profit|\btpt\b/i], ['Bulenox', /bulenox/i],
  ['Earn2Trade', /earn\s*2\s*trade|\be2t\b/i], ['Elite Trader Funding', /elite\s*trader|\betf\b/i], ['Alpha Futures', /alpha\s*futures/i],
  ['FTMO', /\bftmo\b/i], ['The Trading Pit', /trading\s*pit/i], ['Funded Next', /funded\s*next/i],
];

export const isProp = (a) => a.type === 'Evaluation' || a.type === 'Funded' || a.type === 'Live';
// Accounts that pay out: funded, and live (the step after funded).
export const isFunded = (a) => a.type === 'Funded' || a.type === 'Live';

export function firmOf(account) {
  if (!account) return '';
  const found = FIRMS.find(([, re]) => re.test(account.name || ''));
  if (found) return found[0];
  if (account.platform === 'TopstepX') return 'Topstep';
  return '';
}

const pad = (n) => String(n).padStart(2, '0');
// The same day `months` later, kept inside shorter months (Jan 31 → Feb 28).
function addMonths(key, months) {
  const [y, m, d] = key.split('-').map(Number);
  const total = (m - 1) + months;
  const year = y + Math.floor(total / 12);
  const month = (total % 12 + 12) % 12;
  const last = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return `${year}-${pad(month + 1)}-${pad(Math.min(d, last))}`;
}

// The monthly rebill check: prop accounts still open whose billing day came around this month
// (a month after the evaluation fee was paid, or after the account was added) and whose
// rebill isn't answered or already logged. Each comes with the billing day and the amount
// last paid, to suggest. Accounts that never rebill, or that have a monthly expense, are skipped.
export function rebillsDue(accounts, expenses = [], today = dayKey(new Date())) {
  const out = [];
  for (const a of accounts) {
    if (!isProp(a) || a.type === 'Live' || a.archived || a.attemptOf || a.noRebill || !a.createdAt) continue;
    const linked = expenses.filter((e) => String(e.accountId || '') === String(a._id)).sort((x, y) => x.date.localeCompare(y.date));
    if (linked.some((e) => e.repeat === 'monthly' && !(e.until && e.until < today))) continue;
    const fee = linked.find((e) => e.category === 'evaluation' && e.repeat !== 'monthly');
    const anchor = fee ? fee.date : dayKey(a.createdAt);
    let due = null;
    for (let k = 1; k < 600 && addMonths(anchor, k) <= today; k += 1) due = addMonths(anchor, k);
    if (!due || (a.rebillAnswers || []).some((r) => r.due === due)) continue;
    // A charge already logged within a few days of the billing day counts as the answer.
    const near = (d) => Math.abs(Date.parse(d) - Date.parse(due)) <= 3 * 864e5;
    if (linked.some((e) => e.repeat !== 'monthly' && near(e.date))) continue;
    const paid = [...linked].reverse().find((e) => ['evaluation', 'subscription', 'reset'].includes(e.category) && e.repeat !== 'monthly');
    out.push({ account: a, due, amount: paid ? paid.amount : null, firm: firmOf(a) });
  }
  return out;
}

// Every payment an expense stands for, through `today`: one, or one a month for monthly ones.
export function expandExpenses(expenses, today = dayKey(new Date())) {
  const out = [];
  for (const e of expenses) {
    if (e.repeat !== 'monthly') {
      if (e.date <= today) out.push({ ...e, source: e });
      continue;
    }
    const end = e.until && e.until < today ? e.until : today;
    for (let i = 0, d = e.date; d <= end && i < 600; i += 1, d = addMonths(e.date, i)) out.push({ ...e, date: d, source: e });
  }
  return out;
}

// Payouts: withdrawals from funded and live accounts (past attempts included).
export function payoutsOf(accounts) {
  const out = [];
  for (const a of accounts) {
    if (!isFunded(a)) continue;
    for (const f of a.cashFlows || []) if (f.kind === 'withdrawal') out.push({ date: f.date, amount: f.amount, note: f.note || '', accountId: a._id, firm: firmOf(a) });
  }
  return out.sort((x, y) => x.date.localeCompare(y.date));
}

const inRange = (date, range) => (!range.from || date >= range.from) && (!range.to || date <= range.to);
const firmOfExpense = (e, byId) => e.firm || firmOf(byId.get(String(e.accountId))) || '';

// Spent vs paid out in a range ({ from, to } days, either open): totals, by firm, by month and
// by category.
export function moneySummary(expenses, accounts, range = {}, today) {
  const byId = new Map(accounts.map((a) => [String(a._id), a]));
  const paid = expandExpenses(expenses, today).filter((e) => inRange(e.date, range));
  const payouts = payoutsOf(accounts).filter((p) => inRange(p.date, range));
  const spent = paid.reduce((s, e) => s + e.amount, 0);
  const received = payouts.reduce((s, p) => s + p.amount, 0);

  const firms = {};
  const firm = (name) => (firms[name] = firms[name] || { firm: name, spent: 0, payouts: 0, payoutCount: 0 });
  for (const e of paid) firm(firmOfExpense(e, byId)).spent += e.amount;
  for (const p of payouts) {
    const f = firm(p.firm);
    f.payouts += p.amount;
    f.payoutCount += 1;
  }
  const months = {};
  const month = (key) => (months[key] = months[key] || { month: key, spent: 0, payouts: 0 });
  for (const e of paid) month(e.date.slice(0, 7)).spent += e.amount;
  for (const p of payouts) month(p.date.slice(0, 7)).payouts += p.amount;
  const categories = {};
  for (const e of paid) categories[e.category] = (categories[e.category] || 0) + e.amount;

  return {
    spent, received, net: received - spent, roi: spent ? ((received - spent) / spent) * 100 : null,
    payments: paid, payouts,
    byFirm: Object.values(firms).map((f) => ({ ...f, net: f.payouts - f.spent })).sort((a, b) => b.spent + b.payouts - (a.spent + a.payouts)),
    byMonth: Object.values(months).sort((a, b) => a.month.localeCompare(b.month)),
    byCategory: Object.entries(categories).map(([category, amount]) => ({ category, amount })).sort((a, b) => b.amount - a.amount),
  };
}

const daysBetween = (a, b) => Math.max(0, Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 86400000));

// Every prop account and past attempt: when it started and ended, how long it lasted, its
// trades and P&L, payouts, what was spent on it, and how it ended.
export function accountHistory(accounts, trades, expenses = [], today = dayKey(new Date())) {
  const tradesBy = {};
  for (const t of trades) (tradesBy[String(t.accountId)] = tradesBy[String(t.accountId)] || []).push(t);
  const spentBy = {};
  for (const e of expandExpenses(expenses, today)) if (e.accountId) spentBy[String(e.accountId)] = (spentBy[String(e.accountId)] || 0) + e.amount;
  return accounts.filter(isProp).map((a) => {
    const list = tradesBy[String(a._id)] || [];
    const dates = list.map((t) => dayKey(t.date)).sort();
    const start = a.attemptStartedAt ? dayKey(a.attemptStartedAt) : dates[0] || (a.createdAt ? dayKey(a.createdAt) : today);
    const ended = a.archived || a.attemptOf;
    const end = ended ? (a.attemptEndedAt ? dayKey(a.attemptEndedAt) : dates[dates.length - 1] || start) : null;
    const status = accountStatus(a, list);
    const outcome = a.outcome || (ended ? 'ended' : status && status.risk === 'blown' ? 'blown' : 'active');
    return {
      account: a, firm: firmOf(a), type: a.type, start, end, days: daysBetween(start, end || today),
      tradingDays: new Set(list.map((t) => dayKey(t.date))).size, trades: list.length,
      pnl: list.reduce((s, t) => s + netPnl(t), 0), payouts: (a.cashFlows || []).filter((f) => f.kind === 'withdrawal' && isFunded(a)).reduce((s, f) => s + f.amount, 0),
      spent: spentBy[String(a._id)] || 0, outcome, status,
    };
  }).sort((x, y) => y.start.localeCompare(x.start));
}

// How long accounts that ended in a fail lasted, and whether that's getting better: the
// average of the first few against the last few.
export function lifespan(history) {
  const failed = history.filter((h) => h.outcome === 'failed' || h.outcome === 'blown').sort((a, b) => a.start.localeCompare(b.start));
  if (!failed.length) return null;
  const avg = (list) => list.reduce((s, h) => s + h.days, 0) / list.length;
  const n = Math.min(3, Math.floor(failed.length / 2));
  return {
    count: failed.length, avgDays: avg(failed),
    trend: n >= 2 ? { early: avg(failed.slice(0, n)), recent: avg(failed.slice(-n)), n } : null,
  };
}

// Evaluations: how many were bought, passed, failed and still going.
export function evaluationRecord(history) {
  const evals = history.filter((h) => h.type === 'Evaluation');
  const count = (o) => evals.filter((h) => (Array.isArray(o) ? o.includes(h.outcome) : h.outcome === o)).length;
  const passed = count('passed');
  const failed = count(['failed', 'blown']);
  return { total: evals.length, passed, failed, active: count('active'), passRate: passed + failed ? (passed / (passed + failed)) * 100 : null };
}

// Prop accounts (and past attempts) with no expense tied to them yet, to offer "log what you paid".
export function unpaidAccounts(accounts, expenses) {
  const paid = new Set(expenses.filter((e) => e.accountId).map((e) => String(e.accountId)));
  return accounts.filter((a) => isProp(a) && !paid.has(String(a._id)));
}

// Rows for the accountant: every payment and payout in the range, oldest first.
export function ledgerRows(summary, accounts) {
  const byId = new Map(accounts.map((a) => [String(a._id), a]));
  const rows = [
    ...summary.payments.map((e) => ({ date: e.date, kind: 'expense', category: e.category, firm: firmOfExpense(e, byId), account: (byId.get(String(e.accountId)) || {}).name || '', amount: -e.amount, note: e.note || '' })),
    ...summary.payouts.map((p) => ({ date: p.date, kind: 'payout', category: '', firm: p.firm, account: (byId.get(String(p.accountId)) || {}).name || '', amount: p.amount, note: p.note || '' })),
  ];
  return rows.sort((a, b) => a.date.localeCompare(b.date));
}
